import React from "react";
import { Card, CardBody } from "reactstrap";
import { Link } from "react-router-dom";

/**
 * Single shortcut tile used on module landing pages. Mirrors the visual
 * language of Mafia's MenuPage tile but exposed as a reusable component
 * so every module's landing page (Accounting, Sales, Quality, etc.) can
 * present a consistent grid of quick actions.
 *
 * Pure presentation — receives a `to` (router link) or `onClick` and a
 * label/icon/subtitle/tone. Disabled state is supported so callers can
 * keep the tile visible but lock it for unauthorized users (same pattern
 * as MafiaPage).
 *
 * Props
 *  - icon:      ri-* icon class
 *  - label:     headline text (required)
 *  - subtitle:  helper line under label
 *  - tone:      "primary" | "info" | "success" | "warning" | "danger" |
 *               "secondary" | "dark"  (maps to Velzon's bg-{tone}-subtle / text-{tone})
 *  - to:        router path — when set, the tile renders as a <Link>
 *  - onClick:   click handler — used when `to` isn't set
 *  - disabled:  bool — soft-disables the tile
 *  - disabledReason: string — tooltip text shown on the lock icon
 *  - badge:     optional small text on the top-right (e.g. "New", "12")
 */
const TONE_FALLBACK = "primary";

const ModuleShortcutCard = ({
  icon = "ri-stack-line",
  label,
  subtitle,
  tone = TONE_FALLBACK,
  to,
  onClick,
  disabled = false,
  disabledReason,
  badge,
}) => {
  const toneClass = tone || TONE_FALLBACK;

  const inner = (
    <CardBody className="p-3">
      <div className="d-flex align-items-start gap-3">
        <div className="avatar-sm flex-shrink-0" aria-hidden="true">
          <div
            className={`avatar-title rounded bg-${toneClass}-subtle text-${toneClass} fs-3`}
            style={{ borderRadius: 10 }}
          >
            <i className={icon} />
          </div>
        </div>
        <div className="flex-grow-1 overflow-hidden">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <h6
              className="mb-1 fw-semibold text-truncate"
              style={{ color: "var(--ssw-text-1, #212529)" }}
            >
              {label}
            </h6>
            {badge ? (
              <span
                className={`badge bg-${toneClass}-subtle text-${toneClass} flex-shrink-0`}
                style={{ fontSize: 11 }}
              >
                {badge}
              </span>
            ) : null}
          </div>
          {subtitle ? (
            <p
              className="text-muted small mb-0"
              style={{ fontSize: 12 }}
            >
              {subtitle}
            </p>
          ) : null}
        </div>
        {!disabled ? (
          <i
            className="ri-arrow-right-s-line text-muted flex-shrink-0 dk-shortcut-arrow"
            style={{ fontSize: 22 }}
            aria-hidden="true"
          />
        ) : (
          <i
            className="ri-lock-2-line text-muted flex-shrink-0"
            style={{ fontSize: 18, opacity: 0.6 }}
            aria-hidden="true"
            title={disabledReason || "You don't have permission for this action"}
          />
        )}
      </div>
    </CardBody>
  );

  const baseStyle = {
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    transition:
      "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
    border: "1px solid transparent",
    borderRadius: 12,
    textDecoration: "none",
    color: "inherit",
    display: "block",
  };

  const cardClass = `h-100 dk-shortcut-card ${disabled ? "dk-shortcut-disabled" : ""}`;

  // Disabled → render as a plain Card with no interaction.
  if (disabled) {
    return (
      <>
        <Card className={cardClass} style={baseStyle}>
          {inner}
        </Card>
        <ShortcutCardStyles />
      </>
    );
  }

  // Internal route → react-router Link (preserves SPA navigation).
  if (to) {
    return (
      <>
        <Card className={cardClass} style={baseStyle}>
          <Link
            to={to}
            style={{ textDecoration: "none", color: "inherit", display: "block" }}
            aria-label={label}
          >
            {inner}
          </Link>
        </Card>
        <ShortcutCardStyles />
      </>
    );
  }

  // Action callback → button-like card.
  return (
    <>
      <Card
        className={cardClass}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        style={baseStyle}
      >
        {inner}
      </Card>
      <ShortcutCardStyles />
    </>
  );
};

// Inline hover styles — mirrors Mafia's MenuPage tile interaction so the
// landing pages feel identical across modules.
const ShortcutCardStyles = () => (
  <style>{`
    .dk-shortcut-card:hover:not(.dk-shortcut-disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(15, 23, 42, 0.08) !important;
      border-color: rgba(64, 81, 137, 0.18) !important;
    }
    .dk-shortcut-card:hover:not(.dk-shortcut-disabled) .dk-shortcut-arrow {
      color: var(--ssw-ink-blue, #405189) !important;
      transform: translateX(2px);
      transition: transform 0.15s ease, color 0.15s ease;
    }
    .dk-shortcut-disabled {
      pointer-events: none;
    }
  `}</style>
);

export default React.memo(ModuleShortcutCard);
